import { MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";

type VeeraThreadListEntry = {
  id: string;
  title: string;
  updatedAt: string;
  preview?: string;
  tagLabel?: string;
};

interface VeeraThreadListProps {
  threads: VeeraThreadListEntry[];
  activeThreadId: string | null;
  onThreadSelect: (id: string) => void;
  variant: "compact" | "detailed";
  disabled?: boolean;
  className?: string;
}

export function VeeraThreadList({
  threads,
  activeThreadId,
  onThreadSelect,
  variant,
  disabled = false,
  className,
}: VeeraThreadListProps) {
  if (threads.length === 0) return null;

  if (variant === "compact") {
    return (
      <div className={cn("space-y-px", className)}>
        {threads.map((thread) => (
          <button
            key={thread.id}
            type="button"
            onClick={() => onThreadSelect(thread.id)}
            disabled={disabled}
            aria-current={activeThreadId === thread.id ? "page" : undefined}
            className={cn(
              "w-full truncate rounded px-2 py-1 text-left text-xs transition-colors",
              activeThreadId === thread.id
                ? "bg-violet-500/15 text-foreground"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
            title={thread.title}
          >
            {thread.title}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-1", className)}>
      {threads.map((thread) => (
        <button
          key={thread.id}
          type="button"
          aria-current={activeThreadId === thread.id ? "page" : undefined}
          className={cn(
            "w-full min-w-0 overflow-hidden text-left rounded-lg border px-3 py-3 transition-colors",
            activeThreadId === thread.id
              ? "bg-primary/10 border-primary/40 dark:bg-primary/15 dark:border-primary/30"
              : "bg-transparent border-transparent hover:bg-muted/50",
          )}
          onClick={() => onThreadSelect(thread.id)}
          disabled={disabled}
        >
          <div className="flex min-w-0 items-center gap-2">
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="min-w-0 flex-1 truncate pr-1 text-sm font-medium">{thread.title}</p>
          </div>
        </button>
      ))}
    </div>
  );
}
